import { ConfigProvider, Flex, Input, Segmented, Tooltip } from 'antd';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import _, { debounce } from 'lodash';
import { ArrowLeft, ListFilter, MessageCircleOff, MessageSquareDot, Search } from 'lucide-react';
import { useCallback, useState } from 'react';
import ChatList from '../components/ChatList';
import UserSearchSelect from '../components/UserSearchSelect';
import { getChatContacts, getContacts } from '../redux/actions/contact';
import useAuth from '../hooks/useAuth';
import { useSelector } from 'react-redux';
import { store } from '../redux/store';
import { setFilters, setIsChat } from '../redux/slices/filtersSlice';

const ChatLayout = ({ children, className }) => {
  const {
    user: { role },
  } = useAuth();
  const { filters: filterData, isChat } = useSelector((state) => state.filtersSlice);
  const [filter, setFilter] = useState({ search: false, list: false });

  const resetFilter = () => {
    setFilter({ search: false, list: false });
    store.dispatch(setFilters(null));
  };

  const handleOptionsChange = (value) => {
    resetFilter();
    store.dispatch(setIsChat(value === 1));
  };

  const handleFilterChange = (userId) => {
    const updatedFilters = { assigned_user_id: userId };
    store.dispatch(setFilters(updatedFilters));

    if (isChat) {
      getChatContacts({ assigned_user_id: userId });
    } else {
      getContacts({ type: 'CHAT', status: 1, assigned_user_id: userId });
    }
  };

  const fetchUsers = useCallback(
    debounce((searchValue) => {
      if (isChat) {
        getChatContacts({ search: searchValue });
      } else {
        getContacts({ type: 'CHAT', search: searchValue, status: 1 });
      }
    }, 500),
    [isChat]
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    store.dispatch(setFilters({ search: value }));
    fetchUsers(value);
  };

  return (
    <div className={clsx('Chat', className)}>
      <div className="Chat__ChatList">
        {/* Header */}
        <div className="Chat__Header List_Header">
          <Flex flex={1} justify="space-between" align="center" gap={10} className="top-bar">
            {/* Left Title / Back Icon */}
            <AnimatePresence mode="wait">
              {_.some(_.pick(filter, Object.keys(filter))) ? (
                <motion.div
                  key="back-icon"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowLeft
                    size={22}
                    cursor="pointer"
                    onClick={() => {
                      resetFilter();
                      if (filterData?.search || filterData?.assigned_user_id) {
                        fetchUsers();
                      }
                    }}
                  />
                </motion.div>
              ) : (
                <motion.span
                  key="title"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  Chats
                </motion.span>
              )}
            </AnimatePresence>

            {/* Right Actions */}
            <AnimatePresence mode="wait">
              {!_.some(_.pick(filter, Object.keys(filter))) && (
                <motion.div
                  key="search-icon"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  className="icon-container"
                >
                  {isChat && (
                    <>
                      <Tooltip title="Not Responded" arrow={false}>
                        <MessageCircleOff
                          size={22}
                          color={
                            filterData?.not_responded ? 'var(--primary-color)' : 'rgb(156,163,175)'
                          }
                          cursor="pointer"
                          onClick={() => {
                            store.dispatch(
                              setFilters({
                                not_responded: !filterData?.not_responded,
                                unread: false,
                              })
                            );
                            getChatContacts({ not_responded: !filterData?.not_responded });
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Unread" arrow={false}>
                        <MessageSquareDot
                          size={22}
                          color={filterData?.unread ? 'var(--primary-color)' : 'rgb(156,163,175)'}
                          cursor="pointer"
                          onClick={() => {
                            store.dispatch(
                              setFilters({ unread: !filterData?.unread, not_responded: false })
                            );
                            getChatContacts({ unread: !filterData?.unread });
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                  {role !== 2 && (
                    <Tooltip title="User Filter" arrow={false}>
                      <ListFilter
                        size={22}
                        color="rgb(156,163,175)"
                        cursor="pointer"
                        onClick={() => setFilter({ ...filter, list: true })}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Search" arrow={false}>
                    <Search
                      size={22}
                      color="rgb(156,163,175)"
                      cursor="pointer"
                      onClick={() => setFilter({ ...filter, search: true })}
                    />
                  </Tooltip>
                </motion.div>
              )}

              {/* User Filter Dropdown */}
              {filter?.list && role !== 2 && (
                <motion.div
                  key="list-filter"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  className="list-filter"
                >
                  <UserSearchSelect
                    size="middle"
                    value={filterData?.assigned_user_id}
                    onChange={handleFilterChange}
                  />
                </motion.div>
              )}

              {/* Search Bar */}
              {filter?.search && (
                <motion.div
                  key="search-bar"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  style={{ flex: 1 }}
                >
                  <Input
                    placeholder="Search..."
                    allowClear
                    prefix={<Search size={18} color="rgb(156,163,175)" />}
                    value={filterData?.search}
                    onChange={handleSearch}
                    autoFocus
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Flex>

          {/* Segmented Control */}
          <div className="Chat__Header__Actions">
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    itemSelectedBg: 'var(--primary-color)',
                    itemSelectedColor: '#fff',
                    borderRadius: 0,
                  },
                },
              }}
            >
              <Segmented
                options={[
                  { label: 'Chats', value: 1 },
                  { label: 'Contacts', value: 2 },
                ]}
                block
                defaultValue={1}
                value={isChat ? 1 : 2}
                onChange={handleOptionsChange}
              />
            </ConfigProvider>
          </div>
        </div>

        {/* Chat List */}
        <ChatList isChat={isChat} filters={filterData} />
      </div>

      {/* Chat Window */}
      <div className="Chat__Window">{children}</div>
    </div>
  );
};

export default ChatLayout;
